import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  initDB,
  saveInspectionOffline,
  getAllInspectionsOffline,
  saveChecklistOffline,
  getAllChecklistsOffline,
  addToPendingSync,
  getPendingSyncItems,
  removePendingSyncItem,
  getOfflineStats
} from '../lib/offlineDb';
import { inspectionsApi, checklistsApi } from '../lib/api';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize IndexedDB
  useEffect(() => {
    initDB().then(() => {
      setIsInitialized(true);
      updatePendingCount();
    }).catch(err => {
      console.error('Failed to initialize offline database:', err);
    });
  }, []);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Verbindung wiederhergestellt', {
        description: 'Daten werden synchronisiert...'
      });
      syncData();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Offline-Modus', {
        description: 'Änderungen werden lokal gespeichert'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check if regular user is logged in (not super-admin)
  const isRegularUserLoggedIn = () => {
    const token = localStorage.getItem('token');
    const superAdminToken = localStorage.getItem('superAdminToken');
    return token && !superAdminToken;
  };

  // Automatic background sync every 2 minutes when online
  useEffect(() => {
    let syncInterval;
    
    if (isOnline && isInitialized && isRegularUserLoggedIn()) {
      // Initial sync after 5 seconds
      const initialSync = setTimeout(() => {
        if (isRegularUserLoggedIn()) {
          syncData();
        }
      }, 5000);
      
      // Then sync every 2 minutes
      syncInterval = setInterval(() => {
        if (isRegularUserLoggedIn()) {
          console.log('[Offline] Automatic background sync...');
          syncData();
        }
      }, 2 * 60 * 1000); // 2 minutes
      
      return () => {
        clearTimeout(initialSync);
        clearInterval(syncInterval);
      };
    }
    
    return () => {
      if (syncInterval) clearInterval(syncInterval);
    };
  }, [isOnline, isInitialized]);

  // Listen for service worker sync messages
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_REQUESTED') {
          syncData();
        }
      });
    }
  }, []);

  // Update pending sync count
  const updatePendingCount = useCallback(async () => {
    try {
      const stats = await getOfflineStats();
      setPendingCount(stats.pendingSync);
    } catch (err) {
      console.error('Error getting offline stats:', err);
    }
  }, []);

  // Check if user is logged in (not super-admin)
  const isUserLoggedIn = useCallback(() => {
    const token = localStorage.getItem('token');
    const superAdminToken = localStorage.getItem('superAdminToken');
    // Only return true if regular user token exists and no super-admin token
    // This prevents OfflineContext from interfering with Super-Admin portal
    return token && !superAdminToken;
  }, []);

  // Cache data for offline use
  const cacheForOffline = useCallback(async () => {
    if (!isOnline || !isUserLoggedIn()) return;

    try {
      // Cache checklists
      const checklistsResponse = await checklistsApi.getAll();
      for (const checklist of checklistsResponse.data) {
        await saveChecklistOffline(checklist);
      }

      // Cache inspections
      const inspectionsResponse = await inspectionsApi.getAll();
      for (const inspection of inspectionsResponse.data) {
        await saveInspectionOffline({ ...inspection, syncStatus: 'synced' });
      }

      setLastSyncTime(new Date().toISOString());
      console.log('Data cached for offline use');
    } catch (err) {
      console.error('Error caching data:', err);
    }
  }, [isOnline, isUserLoggedIn]);

  // Sync pending changes to server
  const syncData = useCallback(async () => {
    // Don't sync if offline, already syncing, or if super-admin is logged in
    const token = localStorage.getItem('token');
    const superAdminToken = localStorage.getItem('superAdminToken');
    if (!isOnline || isSyncing || !token || superAdminToken) return;

    setIsSyncing(true);
    try {
      const pendingItems = await getPendingSyncItems();
      
      for (const item of pendingItems) {
        try {
          switch (item.type) {
            case 'CREATE_INSPECTION':
              await inspectionsApi.create(item.data);
              break;
            case 'UPDATE_INSPECTION':
              await inspectionsApi.update(item.inspectionId, item.data);
              break;
            case 'UPDATE_ITEM':
              await inspectionsApi.updateItem(item.inspectionId, item.itemId, item.data);
              break;
            case 'SAVE_SIGNATURE':
              await inspectionsApi.saveSignature(item.inspectionId, item.data);
              break;
            case 'COMPLETE_INSPECTION':
              await inspectionsApi.complete(item.inspectionId);
              break;
            case 'DELETE_INSPECTION':
              await inspectionsApi.delete(item.inspectionId);
              break;
            default:
              console.warn('Unknown sync type:', item.type);
          }
          
          await removePendingSyncItem(item.id);
        } catch (err) {
          console.error('Error syncing item:', item, err);
          // Keep in queue for retry
        }
      }

      // Refresh cache after sync
      await cacheForOffline();
      await updatePendingCount();
      
      if (pendingItems.length > 0) {
        toast.success('Synchronisierung abgeschlossen', {
          description: `${pendingItems.length} Änderung(en) übertragen`
        });
      }
      
      setLastSyncTime(new Date().toISOString());
    } catch (err) {
      console.error('Sync error:', err);
      toast.error('Synchronisierungsfehler', {
        description: 'Einige Änderungen konnten nicht übertragen werden'
      });
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, cacheForOffline, updatePendingCount]);

  // Queue an action for sync
  const queueForSync = useCallback(async (action) => {
    await addToPendingSync(action);
    await updatePendingCount();
    
    // Try to sync immediately if online
    if (isOnline) {
      syncData();
    }
  }, [isOnline, syncData, updatePendingCount]);

  // Get inspections (from cache if offline)
  const getInspections = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await inspectionsApi.getAll();
        // Update cache
        for (const inspection of response.data) {
          await saveInspectionOffline({ ...inspection, syncStatus: 'synced' });
        }
        return response.data;
      } catch (err) {
        console.log('Online fetch failed, using offline data');
      }
    }
    return getAllInspectionsOffline();
  }, [isOnline]);

  // Get checklists (from cache if offline)
  const getChecklists = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await checklistsApi.getAll();
        // Update cache
        for (const checklist of response.data) {
          await saveChecklistOffline(checklist);
        }
        return response.data;
      } catch (err) {
        console.log('Online fetch failed, using offline data');
      }
    }
    return getAllChecklistsOffline();
  }, [isOnline]);

  // Create inspection (offline-capable)
  const createInspection = useCallback(async (data) => {
    if (isOnline) {
      try {
        const response = await inspectionsApi.create(data);
        await saveInspectionOffline({ ...response.data, syncStatus: 'synced' });
        return response.data;
      } catch (err) {
        console.log('Online create failed, saving offline');
      }
    }

    // Create offline inspection with temporary ID
    const tempId = `offline-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const offlineInspection = {
      ...data,
      id: tempId,
      status: 'draft',
      items: [],
      created_at: new Date().toISOString(),
      syncStatus: 'pending'
    };

    await saveInspectionOffline(offlineInspection);
    await queueForSync({ type: 'CREATE_INSPECTION', data, tempId });

    toast.info('Offline gespeichert', {
      description: 'Wird synchronisiert sobald Verbindung besteht'
    });

    return offlineInspection;
  }, [isOnline, queueForSync]);

  // Update item (offline-capable)
  const updateItem = useCallback(async (inspectionId, itemId, data) => {
    if (isOnline) {
      try {
        const response = await inspectionsApi.updateItem(inspectionId, itemId, data);
        return response.data;
      } catch (err) {
        console.log('Online update failed, queuing for sync');
      }
    }

    await queueForSync({
      type: 'UPDATE_ITEM',
      inspectionId,
      itemId,
      data
    });

    return { success: true, offline: true };
  }, [isOnline, queueForSync]);

  // Save signature (offline-capable)
  const saveSignature = useCallback(async (inspectionId, data) => {
    if (isOnline) {
      try {
        const response = await inspectionsApi.saveSignature(inspectionId, data);
        return response.data;
      } catch (err) {
        console.log('Online signature save failed, queuing for sync');
      }
    }

    await queueForSync({
      type: 'SAVE_SIGNATURE',
      inspectionId,
      data
    });

    return { success: true, offline: true };
  }, [isOnline, queueForSync]);

  const value = {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    isInitialized,
    syncData,
    cacheForOffline,
    queueForSync,
    getInspections,
    getChecklists,
    createInspection,
    updateItem,
    saveSignature
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}
